import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { listarNotificacoes } from '../api/notificacoes';
import { useAuth } from '../lib/auth';

const EMPRESA_NOME = 'Varga Car Service';
const VERSAO = '0.1.0';

interface ItemNav {
  para: string;
  texto: string;
  fim?: boolean;
  soAdmin?: boolean;
  badge?: boolean;
}

const ITENS: ItemNav[] = [
  { para: '/', texto: 'Cadastro', fim: true },
  { para: '/notificacoes', texto: 'Notificações', badge: true },
  { para: '/clientes', texto: 'Clientes' },
  { para: '/vouchers', texto: 'Vouchers' },
  { para: '/dashboard', texto: 'Dashboard' },
  { para: '/campanha', texto: 'Campanha', soAdmin: true },
];

function BadgeNotificacoes(): ReactNode {
  const { data } = useQuery({
    queryKey: ['notificacoes', 'pendente', 'badge'],
    queryFn: () => listarNotificacoes('pendente'),
    refetchInterval: 60_000,
  });
  const n = data?.length ?? 0;
  if (n === 0) return null;
  return (
    <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-1 bg-acento px-1 font-display text-rotulo text-white">
      {n}
    </span>
  );
}

export function Shell({ children }: { children: ReactNode }): ReactNode {
  const { usuario, sair } = useAuth();
  const navigate = useNavigate();
  const ehAdmin = usuario?.role === 'admin';

  const aoSair = async (): Promise<void> => {
    await sair();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-fundo text-tinta">
      <aside className="flex w-60 shrink-0 flex-col border-r border-linha bg-superficie">
        <div className="px-5 py-4 font-display text-[0.9375rem] font-bold tracking-[0.02em] text-tinta-forte">
          Conexão Varga
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 px-2">
          {ITENS.filter((i) => !i.soAdmin || ehAdmin).map((i, idx) => (
            <div key={i.para}>
              {i.soAdmin && <div className="my-1 border-t border-linha" />}
              <NavLink
                to={i.para}
                end={i.fim}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2 rounded-1 px-3 py-2 text-corpo transition-colors',
                    isActive
                      ? 'bg-acento-fraco font-medium text-tinta-forte'
                      : 'text-tinta-2 hover:bg-poco hover:text-tinta-forte',
                  )
                }
              >
                {i.texto}
                {i.badge && <BadgeNotificacoes />}
              </NavLink>
              {idx === 0 && <div className="my-1" />}
            </div>
          ))}
        </nav>
        <div className="border-t border-linha px-5 py-3 text-nota text-tinta-3">
          <div>{EMPRESA_NOME}</div>
          <div className="tnum">v{VERSAO}</div>
          <div className="mt-1 truncate">{usuario?.nome ?? usuario?.id}</div>
          <button
            type="button"
            onClick={() => void aoSair()}
            className="mt-1 font-display text-[0.8125rem] font-bold text-acento-texto underline"
          >
            Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="mx-auto max-w-conteudo px-[var(--gutter)] py-6">{children}</div>
      </main>
    </div>
  );
}

/** Cabeçalho de tela: título à esquerda, ações à direita (UX-PAINEL-INTERNO §0.2). */
export function CabecalhoTela({ titulo, acoes }: { titulo: string; acoes?: ReactNode }): ReactNode {
  return (
    <div className="mb-5 flex items-center justify-between gap-4">
      <h1 className="font-display text-tela-titulo font-bold text-tinta-forte">{titulo}</h1>
      {acoes && <div className="flex items-center gap-2">{acoes}</div>}
    </div>
  );
}
